import DescriptionList from 'ant-design-pro/lib/DescriptionList';
import PageHeader from 'ant-design-pro/lib/PageHeader';
import { Breadcrumb, Button, Card, Col, DatePicker, Empty, message, Pagination, Row } from 'antd';
import { gql } from 'apollo-boost';
import * as moment from 'moment';
import React, { Component } from 'react';
import { Query } from 'react-apollo';
import { Link } from 'react-router-dom';
import { client } from 'src/services/api';
import { mobileMasking } from 'src/utils/masking';
import * as xlsx from 'xlsx';

import { Panel } from '../../components/Panel';
import { formatDate } from '../../components/table/Date';
import { DateRange } from '../../types/base';
import { RedeemCodeRecordType, RedeemCodeTemplate, SkuGroup } from '../../types/redeemCode';
import { RedeemCodeTableList, TableType } from './RedeemCodeTableList';

const { Description } = DescriptionList;

const REDEEM_CODE_QUERY = gql`
  query FindTemplates(
    $channel: String
    $id: ID
    $dateRange: DateRange
    $take: Int
    $skip: Int
    $after: ID
    $before: ID
  ) {
    redeemCodeTemplates(channel: $channel, id: $id) {
      id
      channel
      skuGroupId
      skuId
      skuGroup {
        id
        name
      }
      desc
      duration
      createdAt
      price
      activityPrice
      cost
      marketingCost
      codesConnection(dateRange: $dateRange, take: $take, skip: $skip, after: $after, before: $before) {
        nodes {
          id
          code
          invalidAt
          createdAt
          redeemRecords {
            createdAt
            id
            userId
            invoiceId
            type
            user {
              name
              id
              mobile
            }
          }
        }
        pageInfo {
          totalCount
          startCursor
          endCursor
        }
      }
    }
  }
`;

interface Props {
  match: {
    params: { id: string };
  };
}

interface State {
  redeemCodeCount?: number;
  isOfferCreateRedeemCodeConfirmVisible: boolean;
  currentPage: number;
  rankingsDateRange: DateRange;
  isDestroyRedeemCodePopupConfirmVisible: boolean;
  redeemCodeId?: number;
  pagination: {
    take: number;
    skip?: number;
    before?: number;
    after?: number;
  };
}

export class RedeemCodeDetail extends Component<Props, State> {
  REDEEM_CODES_COUNT_PER_PAGE = 10;
  constructor(props: Props) {
    super(props);
    this.state = {
      isOfferCreateRedeemCodeConfirmVisible: false,
      currentPage: 1,
      rankingsDateRange: {
        from: moment().startOf('month').toDate(),
        to: moment().endOf('day').toDate(),
      },
      pagination: {
        take: 10,
        skip: 0,
      },
      isDestroyRedeemCodePopupConfirmVisible: false,
    };
  }

  onCancelCreateRedeemCodeClick = () => {
    this.setState({ isOfferCreateRedeemCodeConfirmVisible: false });
  };

  onPageChange = (currentPage: number) => {
    this.setState({
      currentPage,
    });
  };

  distroyRedeemCodeConfirm = (id: number) => {
    this.setState({ isDestroyRedeemCodePopupConfirmVisible: true, redeemCodeId: id });
  };

  exportRedeemCodeExcel = async () => {
    const response = await client.query<{ redeemCodeTemplates: RedeemCodeTemplate[] }>({
      query: REDEEM_CODE_QUERY,
      variables: {
        id: this.props.match.params.id,
        dateRange: this.state.rankingsDateRange,
        skip: 0,
        take: 10000,
      },
    });

    if (!response.data) {
      message.error('获取数据失败');
      return;
    }
    message.info('导出红包记录条数最多10000条');

    const { redeemCodeTemplates } = response.data;

    const excelRedeemCodes: {
      兑换码: string;
      过期时间: string;
      创建时间: string;
      兑换日期: string;
      兑换用户: string;
      兑换用户手机号: string;
      发放用户手机号: string;
      模板ID: number;
      渠道名: string;
      skuGroupName?: string;
      skuId?: number;
      市场价: string;
      活动价: string;
      课程成本: string;
      市场课程成本: string;
      描述: string;
    }[] = [];
    for (const redeemCodeTemplate of redeemCodeTemplates) {
      const redeemCodes = (redeemCodeTemplate.codesConnection && redeemCodeTemplate.codesConnection.nodes) || [];
      for (const redeemCode of redeemCodes) {
        const { redeemRecords } = redeemCode;
        const used = redeemRecords.find(record => record.type === RedeemCodeRecordType.USED);
        const allocated = redeemCode.redeemRecords.find(record => record.type === RedeemCodeRecordType.ALLOCATED);
        const { createdAt, user } = used || { createdAt: '', user: { name: '-', mobile: '-' } };

        const userName = (user && user.name) || '-';
        const maskedMobile = user && user.mobile !== '-' ? mobileMasking(user.mobile) : '-';
        const maskedAllocatedTel = (allocated && allocated.user && mobileMasking(allocated.user.mobile)) || '-';

        excelRedeemCodes.push({
          兑换码: redeemCode.code,
          过期时间: formatDate(redeemCode.invalidAt, 'datetime'),
          创建时间: formatDate(redeemCode.createdAt, 'datetime'),
          兑换日期: formatDate(createdAt, 'datetime'),
          兑换用户: userName,
          兑换用户手机号: maskedMobile,
          发放用户手机号: maskedAllocatedTel,
          模板ID: redeemCodeTemplate.id,
          渠道名: redeemCodeTemplate.channel,
          skuGroupName: redeemCodeTemplate.skuGroup && redeemCodeTemplate.skuGroup.name,
          skuId: redeemCodeTemplate.skuId && redeemCodeTemplate.skuId,
          市场价: (redeemCodeTemplate.price && redeemCodeTemplate.price.toString()) || '-',
          活动价: (redeemCodeTemplate.activityPrice && redeemCodeTemplate.activityPrice.toString()) || '-',
          课程成本: (redeemCodeTemplate.cost && redeemCodeTemplate.cost.toString()) || '-',
          市场课程成本: (redeemCodeTemplate.marketingCost && redeemCodeTemplate.marketingCost.toString()) || '-',
          描述: redeemCodeTemplate.desc,
        });
      }
    }
    const workbook = xlsx.utils.book_new();
    const redeemCodeList = xlsx.utils.json_to_sheet(excelRedeemCodes);
    xlsx.utils.book_append_sheet(workbook, redeemCodeList);
    xlsx.writeFile(workbook, 'redeemCode.xlsx');
  };
  render() {
    const { rankingsDateRange } = this.state;
    return (
      <>
        <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
          <Breadcrumb.Item>
            <Link to={`/Home`}>首页</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/redeem-code-template/templatelist`}>兑换码模板</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>模板详情</Breadcrumb.Item>
        </Breadcrumb>
        <Query
          query={REDEEM_CODE_QUERY}
          variables={{ id: this.props.match.params.id, dateRange: rankingsDateRange, ...this.state.pagination }}
          fetchPolicy="cache-and-network"
        >
          {({ error, data }) => {
            if (error) return <div>{error.message}</div>;

            const [redeemCodeTemplate]: RedeemCodeTemplate[] = (data && data.redeemCodeTemplates) || [];
            if (!redeemCodeTemplate) {
              // @ts-ignore
              return <Empty />;
            }

            const { codesConnection } = redeemCodeTemplate;
            const redeemCodes = (codesConnection && codesConnection.nodes) || [];

            return (
              <Panel style={{ marginTop: 16 }}>
                <PageHeader
                  title={<div>模板信息</div>}
                  content={
                    <DescriptionList size="small" title="">
                      <Description term="模板ID">{redeemCodeTemplate.id}</Description>
                      <Description term="渠道">{redeemCodeTemplate.channel}</Description>
                      <Description term="描述">{redeemCodeTemplate.desc}</Description>
                      <Description term="skuGroupName">
                        {redeemCodeTemplate.skuGroup && redeemCodeTemplate.skuGroup.name}
                      </Description>
                      <Description term="skuId">{redeemCodeTemplate.skuId && redeemCodeTemplate.skuId}</Description>
                      <Description term="有效期">
                        生成{moment.duration(redeemCodeTemplate.duration, 'seconds').humanize()}内有效
                      </Description>
                    </DescriptionList>
                  }
                />
                <Card bordered={false}>
                  <Row gutter={24}>
                    <Col span={12}>
                      <span>
                        生成时间范围：
                        <DatePicker.RangePicker
                          showTime={{ format: 'HH:mm' }}
                          format="YYYY-MM-DD HH:mm"
                          value={[moment(rankingsDateRange.from), moment(rankingsDateRange.to)]}
                          ranges={{
                            今天: [moment(), moment()],
                            本周: [moment().startOf('week'), moment().endOf('week')],
                            上周: [
                              moment().startOf('week').subtract(1, 'week'),
                              moment().endOf('week').subtract(1, 'week'),
                            ],
                          }}
                          onChange={dates =>
                            this.setState({
                              rankingsDateRange: {
                                from: moment(dates[0]).toDate(),
                                to: moment(dates[1]).toDate(),
                              },
                            })
                          }
                        />
                      </span>
                    </Col>
                    <Col span={2}>
                      <Button
                        type="primary"
                        icon="download"
                        onClick={async () => {
                          await this.exportRedeemCodeExcel();
                        }}
                      >
                        导出
                      </Button>
                    </Col>
                  </Row>
                </Card>

                <RedeemCodeTableList
                  refetchQueries={[
                    {
                      query: REDEEM_CODE_QUERY,
                      variables: {
                        id: this.props.match.params.id,
                        dateRange: rankingsDateRange,
                        ...this.state.pagination,
                      },
                    },
                  ]}
                  redeemCodes={redeemCodes}
                  tabelListType={TableType.REDEEMCODEDETAIL}
                  price={redeemCodeTemplate.price}
                  activityPrice={redeemCodeTemplate.activityPrice}
                  cost={redeemCodeTemplate.cost}
                  marketingCost={redeemCodeTemplate.marketingCost}
                  footer={() => {
                    if (codesConnection!.pageInfo.totalCount === 0) {
                      return null;
                    }
                    return (
                      <div>
                        <Pagination
                          total={codesConnection!.pageInfo.totalCount}
                          pageSize={this.state.pagination.take}
                          current={this.state.currentPage}
                          onChange={nextPage => {
                            const { currentPage } = this.state;
                            const skip = (Math.abs(nextPage - currentPage) - 1) * this.REDEEM_CODES_COUNT_PER_PAGE;

                            if (nextPage > currentPage) {
                              this.setState({
                                currentPage: nextPage,
                                pagination: {
                                  skip,
                                  take: this.REDEEM_CODES_COUNT_PER_PAGE,
                                  after: codesConnection!.pageInfo.endCursor,
                                },
                              });
                            }
                            if (nextPage < currentPage) {
                              this.setState({
                                currentPage: nextPage,
                                pagination: {
                                  skip,
                                  take: this.REDEEM_CODES_COUNT_PER_PAGE,
                                  before: codesConnection!.pageInfo.startCursor,
                                },
                              });
                            }
                          }}
                        />
                      </div>
                    );
                  }}
                />
              </Panel>
            );
          }}
        </Query>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/redeemCode/RedeemCodeDetail.tsx